import { useMemo, useCallback, useEffect, useRef } from "react";

import { useMergedRefs } from "~/hooks/useMergedRefs";

import { TranscriptNode } from "./TranscriptNode";

import "./Transcript.css";

import type { RefObject } from "react";
import type {
  TranscriptData,
  TranscriptEntry,
  TranscriptSegment,
} from "~/utils/transcript";

export interface TranscriptProps {
  activeSegmentRef?: RefObject<HTMLParagraphElement>;
  currentTimestamp?: number;
  data?: TranscriptData;
  preventAutoScroll?: boolean;
  scrollBehavior?: ScrollBehavior;
  selectedNodeIds?: string[];
  onSelect?: (id: string) => void;
}

export function TranscriptContent({
  activeSegmentRef: externalActiveSegmentRef,
  currentTimestamp = 0,
  data,
  preventAutoScroll,
  scrollBehavior,
  selectedNodeIds,
  onSelect,
}: TranscriptProps) {
  const activeSegmentRef = useRef<HTMLParagraphElement>(null);
  const mergedActiveSegmentRefs = useMergedRefs(
    activeSegmentRef,
    externalActiveSegmentRef,
  );

  const handleNodeClick = useCallback(
    (id: string) => {
      onSelect?.(id);
    },
    [onSelect],
  );

  // Scroll segment into view
  useEffect(() => {
    if (preventAutoScroll) return;
    activeSegmentRef.current?.scrollIntoView({
      behavior: scrollBehavior,
      block: "center",
    });
  }, [currentTimestamp, preventAutoScroll, scrollBehavior]);

  const renderedNodes = useMemo(() => {
    return data?.segments.map((segment) => {
      const isIntervalCurrent = (
        interval: TranscriptSegment | TranscriptEntry,
        currentTime: number,
      ) => {
        return (
          interval.start <= currentTimestamp && interval.end >= currentTimestamp
        );
      };

      const isCurrent = isIntervalCurrent(segment, currentTimestamp);

      return (
        <p
          key={segment.start}
          className="TranscriptContent__group"
          aria-current={isCurrent}
        >
          {segment.entries.map((entry) => {
            const isEntryCurrent = isIntervalCurrent(entry, currentTimestamp);

            // Wrap multiple nodes
            if (entry.nodes.length > 1) {
              return entry.nodes.map((node, i) => {
                return (
                  <span key={node.id} className="TranscriptContent__group">
                    <TranscriptNode
                      ref={
                        isEntryCurrent && i == 0
                          ? mergedActiveSegmentRefs
                          : undefined
                      }
                      isSelected={selectedNodeIds?.includes(node.id)}
                      isCurrent={isEntryCurrent}
                      scrollBehavior={scrollBehavior}
                      onClick={() => handleNodeClick(node.id)}
                    >
                      {node.text}
                    </TranscriptNode>
                  </span>
                );
              });
            }

            return entry.nodes.map((node, i, a) => {
              return (
                <TranscriptNode
                  key={node.id}
                  ref={
                    isEntryCurrent && i == 0
                      ? mergedActiveSegmentRefs
                      : undefined
                  }
                  isCurrent={isEntryCurrent}
                  isSelected={selectedNodeIds?.includes(node.id)}
                  scrollBehavior={scrollBehavior}
                  onClick={() => handleNodeClick(node.id)}
                >
                  {`${node.text.concat(" ")}`}
                </TranscriptNode>
              );
            });
          })}
        </p>
      );
    });
  }, [
    data?.segments,
    currentTimestamp,
    mergedActiveSegmentRefs,
    selectedNodeIds,
    scrollBehavior,
    handleNodeClick,
  ]);

  return <div className="TranscriptContent">{renderedNodes}</div>;
}
